import {
  ConversationMessageSource,
  ConversationParticipantRole,
  ConversationStatus,
  ConversationSummaryDTO,
  ConversationSupportStatus,
  ConversationType,
  LastMessageSenderKind
} from 'models/conversations'

import { Nullable } from 'models/helpers'

export enum ConversationListStatus {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  AWAITING_YOU = 'awaiting_you',
  RESOLVED = 'resolved',
  CLOSED = 'closed',
}

const SUPPORT_STATUS_LIST_STATUS: Record<ConversationSupportStatus, ConversationListStatus> = {
  [ConversationSupportStatus.IN_PROGRESS]: ConversationListStatus.IN_PROGRESS,
  [ConversationSupportStatus.WAITING_FOR_YOU]: ConversationListStatus.AWAITING_YOU,
  [ConversationSupportStatus.RESOLVED]: ConversationListStatus.RESOLVED,
}

const timeOf = (value: Nullable<string> | undefined) => (value ? Date.parse(value) : Number.NaN)

export const isLastMessageAfterSupportStatusChange = (conversation: ConversationSummaryDTO): boolean => {
  const changedAt = timeOf(conversation.supportStatusChangedAt)
  const lastMessageAt = timeOf(conversation.lastMessageAt)
  return Number.isNaN(changedAt) || Number.isNaN(lastMessageAt) || lastMessageAt > changedAt
}

const isHumanAgentReply = (conversation: ConversationSummaryDTO) =>
  conversation.lastMessageSource === ConversationMessageSource.SLACK ||
  conversation.lastMessageSource === ConversationMessageSource.SALESFORCE

const hasAgentWorkingOnIt = (conversation: ConversationSummaryDTO) =>
  conversation.status === ConversationStatus.ESCALATED ||
  (conversation.type === ConversationType.AGENT_CLIENT &&
    conversation.participants.some((participant) => participant.role === ConversationParticipantRole.AGENT && !participant.leftAt))

export const getConversationListStatus = (
  conversation: ConversationSummaryDTO,
  currentUserId: string | undefined
): ConversationListStatus => {
  if (conversation.status === ConversationStatus.CLOSED) return ConversationListStatus.CLOSED
  const fromSupportStatus = conversation.supportStatus ? SUPPORT_STATUS_LIST_STATUS[conversation.supportStatus] : undefined
  const isLastFromCaller = conversation.lastMessageSenderKind
    ? conversation.lastMessageSenderKind === LastMessageSenderKind.YOU
    : !!currentUserId && conversation.lastMessageSenderId === currentUserId
  if (fromSupportStatus === ConversationListStatus.AWAITING_YOU && isLastFromCaller && isLastMessageAfterSupportStatusChange(conversation)) {
    return ConversationListStatus.IN_PROGRESS
  }
  if (fromSupportStatus) return fromSupportStatus
  const isLastFromSomeoneElse = !!currentUserId && !!conversation.lastMessageSenderId &&
    conversation.lastMessageSenderId !== currentUserId
  const isAwaitingHuman = conversation.status === ConversationStatus.ESCALATED && !isHumanAgentReply(conversation)
  if (isLastFromSomeoneElse && !isAwaitingHuman) return ConversationListStatus.AWAITING_YOU
  if (hasAgentWorkingOnIt(conversation)) return ConversationListStatus.IN_PROGRESS
  return ConversationListStatus.OPEN
}

export const mergeForConversationList = (
  ongoing: ConversationSummaryDTO[],
  closed: ConversationSummaryDTO[]
): ConversationSummaryDTO[] => {
  const closedIds = new Set(closed.map((conversation) => conversation.id))
  const notInClosedPage = ongoing.filter((conversation) => !closedIds.has(conversation.id))
  return [
    ...notInClosedPage.filter((conversation) => conversation.status !== ConversationStatus.CLOSED),
    ...notInClosedPage.filter((conversation) => conversation.status === ConversationStatus.CLOSED),
    ...closed,
  ]
}
